/**
 * ТЗ-2026-002 — длительность отгула по рабочему графику дня. Зеркало бэка (as-life-api
 * src/edo/time-off-hours.ts): сервер авторитетен, фронт считает то же для живой длительности и
 * блокировки отправки. Правила: рабочее окно [start..end] (18:00 обычный / 17:00 предпраздничный),
 * обед 13:00–14:00 вычитается, время вне окна отклоняется. workHours из производственного календаря.
 */
import { minToHm } from '../../lib/date'

/** Рабочий график: минуты от местной полуночи (TZ здесь не нужен — форма уже в местном времени). */
const TIMEOFF_CONFIG = {
  startMin: 9 * 60,
  lunchStartMin: 13 * 60,
  lunchEndMin: 14 * 60,
}

export interface WorkdayWindow {
  startMin: number
  endMin: number
  lunchStartMin: number
  lunchEndMin: number
}

export type EvalCode = 'ORDER' | 'BEFORE_START' | 'AFTER_END' | 'EMPTY'
export type EvalResult = { ok: true; hours: number } | { ok: false; code: EvalCode }

/** Пересечение полуинтервалов [a1,a2) и [b1,b2) в минутах (≥ 0). */
function overlapMinutes(a1: number, a2: number, b1: number, b2: number): number {
  return Math.max(0, Math.min(a2, b2) - Math.max(a1, b1))
}

/** `HH:MM` из минут от полуночи. */
export const fmtHm = minToHm

/** Рабочее окно дня из числа рабочих часов календаря (8 → 09:00–18:00, 7 → 09:00–17:00). */
export function windowFor(workHours: number): WorkdayWindow {
  const { startMin, lunchStartMin, lunchEndMin } = TIMEOFF_CONFIG
  return {
    startMin,
    endMin: startMin + workHours * 60 + (lunchEndMin - lunchStartMin),
    lunchStartMin,
    lunchEndMin,
  }
}

/**
 * Длительность отгула [fromMin, toMin] в рабочих часах: пересечение с окном минус обед.
 * Вне окна — BEFORE_START/AFTER_END; конец ≤ начала — ORDER; чистый ноль (только обед) — EMPTY.
 */
export function evaluateTimeOff(fromMin: number, toMin: number, w: WorkdayWindow): EvalResult {
  if (toMin <= fromMin) return { ok: false, code: 'ORDER' }
  if (fromMin < w.startMin) return { ok: false, code: 'BEFORE_START' }
  if (toMin > w.endMin) return { ok: false, code: 'AFTER_END' }
  const lunch = overlapMinutes(fromMin, toMin, w.lunchStartMin, w.lunchEndMin)
  const minutes = toMin - fromMin - lunch
  if (minutes <= 0) return { ok: false, code: 'EMPTY' }
  return { ok: true, hours: minutes / 60 }
}
